import { useTranslation } from 'react-i18next';

function StarShape({
  x,
  y,
  size = 14,
  opacity = 0.9,
}: {
  x: number;
  y: number;
  size?: number;
  opacity?: number;
}) {
  const h = size / 2;
  const q = size / 4;

  return (
    <path
      d={`M${x},${y - h} L${x + q},${y - q} L${x + h},${y} L${x + q},${y + q} L${x},${y + h} L${x - q},${y + q} L${x - h},${y} L${x - q},${y - q} Z`}
      fill="#fbbf24"
      opacity={opacity}
    />
  );
}

StarShape.displayName = 'StarShape';

// Illustration: Music IQ chat bar

export function IntroIllustration() {
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'musicIqIntroModal.introIllustration',
  });

  return (
    <div className="relative flex w-full items-center justify-center py-xs">
      {/* Decorative stars */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 480 260"
        preserveAspectRatio="xMidYMid meet"
      >
        <g opacity="0">
          <StarShape x={22} y={40} size={16} />
          <StarShape x={14} y={66} size={9} opacity={0.6} />
          <StarShape x={462} y={28} size={14} />
          <StarShape x={472} y={56} size={8} opacity={0.6} />
          <StarShape x={440} y={220} size={10} opacity={0.5} />
          <animate
            attributeName="opacity"
            values="0;1"
            dur="0.4s"
            begin="0.7s"
            fill="freeze"
          />
        </g>
      </svg>

      <div style={{ width: '88%', maxWidth: '500px' }}>
        <svg
          viewBox="0 0 520 280"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full overflow-visible drop-shadow-2xl"
        >
          <defs>
            <radialGradient id="il5-glow-blue" cx="30%" cy="50%" r="60%">
              {/* var() is unreliable in SVG presentation attributes, so the
                    token comes through inline style instead. */}
              <stop
                offset="0%"
                style={{ stopColor: 'oklch(var(--bg-primary-default) / 0.55)' }}
              />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <radialGradient id="il5-glow-lime" cx="70%" cy="50%" r="60%">
              <stop
                offset="0%"
                style={{
                  stopColor: 'oklch(var(--bg-tertiary-default) / 0.45)',
                }}
              />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <linearGradient id="il5-border" x1="0" y1="0" x2="1" y2="1">
              <stop
                offset="0%"
                style={{ stopColor: 'oklch(var(--bg-primary-default))' }}
              />
              <stop
                offset="50%"
                style={{ stopColor: 'oklch(var(--bg-tertiary-default))' }}
              />
              <stop
                offset="100%"
                style={{ stopColor: 'oklch(var(--bg-primary-default))' }}
              />
            </linearGradient>
            <clipPath id="il5-pill-clip">
              <rect x="40" y="214" width="440" height="52" rx="26" />
            </clipPath>
          </defs>

          {/* 1 — Ambient glows */}
          <g opacity="0">
            <ellipse
              cx="160"
              cy="200"
              rx="160"
              ry="90"
              fill="url(#il5-glow-blue)"
              opacity="0.7"
            />
            <ellipse
              cx="370"
              cy="200"
              rx="160"
              ry="90"
              fill="url(#il5-glow-lime)"
              opacity="0.6"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.5s"
              begin="0s"
              fill="freeze"
            />
          </g>

          {/* 2 — Chat response card shell */}
          <g opacity="0">
            <rect
              x="60"
              y="18"
              width="400"
              height="180"
              rx="14"
              fill="rgba(255,255,255,0.04)"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="1"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="0.18s"
              fill="freeze"
            />
          </g>

          {/* 3 — Card header: icon + Music IQ label + divider */}
          <g opacity="0">
            <image
              href="/images/music-iq/music-iq.svg"
              x="76"
              y="34"
              width="24"
              height="24"
              preserveAspectRatio="xMidYMid meet"
            />
            <text
              x="110"
              y="51"
              fill="rgba(255,255,255,0.55)"
              fontSize="11"
              fontFamily="sans-serif"
            >
              {t('title')}
            </text>
            <line
              x1="74"
              y1="70"
              x2="446"
              y2="70"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="0.36s"
              fill="freeze"
            />
          </g>

          {/* 4 — Question bubble */}
          <g opacity="0">
            <rect
              x="280"
              y="82"
              width="154"
              height="26"
              rx="13"
              fill="rgba(37,86,234,0.30)"
            />
            <text
              x="296"
              y="99"
              fill="rgba(255,255,255,0.75)"
              fontSize="10.5"
              fontFamily="sans-serif"
            >
              {t('question')}
            </text>
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="0.52s"
              fill="freeze"
            />
          </g>

          {/* 5 — Answer lines */}
          <g opacity="0">
            <rect
              x="74"
              y="120"
              width="220"
              height="9"
              rx="4.5"
              fill="rgba(255,255,255,0.18)"
            />
            <rect
              x="74"
              y="136"
              width="270"
              height="9"
              rx="4.5"
              fill="rgba(255,255,255,0.13)"
            />
            <rect
              x="74"
              y="152"
              width="180"
              height="9"
              rx="4.5"
              fill="rgba(255,255,255,0.09)"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="0.66s"
              fill="freeze"
            />
          </g>

          {/* 6 — Typing cursor (blinks after it appears) */}
          <g opacity="0">
            <rect x="74" y="168" width="3" height="10" rx="1.5" fill="#56E689">
              <animate
                attributeName="opacity"
                values="0.8;0.1;0.8"
                dur="1.1s"
                begin="0.82s"
                repeatCount="indefinite"
              />
            </rect>
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.2s"
              begin="0.80s"
              fill="freeze"
            />
          </g>

          {/* 7 — Chat pill border + fill */}
          <g opacity="0">
            <rect
              x="38"
              y="212"
              width="444"
              height="56"
              rx="28"
              fill="url(#il5-border)"
              opacity="0.85"
            />
            <rect
              x="41"
              y="215"
              width="438"
              height="50"
              rx="25"
              fill="#1c1c1c"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="0.90s"
              fill="freeze"
            />
          </g>

          {/* 8 — Pill contents: icon + placeholder + send */}
          <g opacity="0">
            <image
              href="/images/music-iq/music-iq.svg"
              x="58"
              y="228"
              width="24"
              height="24"
              preserveAspectRatio="xMidYMid meet"
            />
            <text
              x="96"
              y="245"
              fill="rgba(255,255,255,0.35)"
              fontSize="12"
              fontFamily="sans-serif"
            >
              {t('inputPlaceholder')}
            </text>
            <circle cx="452" cy="240" r="17" fill="rgba(255,255,255,0.12)" />
            <path
              d="M445 240 l7 -6 7 6 M452 234 l0 12"
              stroke="rgba(255,255,255,0.45)"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <animate
              attributeName="opacity"
              values="0;1"
              dur="0.35s"
              begin="1.02s"
              fill="freeze"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

IntroIllustration.displayName = 'IntroIllustration';
